import { useEffect, useRef, useState } from 'react'
import { Bell, BellOff, Pencil, Plus, RefreshCw, Trash2 } from 'lucide-react'
import {
  EmptyRow, ErpTable, Panel, SectionHeader, Td, Th,
} from '@/components/erp/primitives'
import { Field, Modal } from '@/shared/ui/Modal'
import { AsyncMultiSelect } from '@/shared/ui/filters'
import { ErrorStrip, Forbidden, LoadingBlock, Note } from '@/shared/ui/states'
import { useLoad } from '@/features/administration/hooks'
import { useCan, useSession } from '@/shared/auth/session'
import { explain } from '@/shared/api/problem'
import { fmtDateTime, fmtNumber } from '@/shared/lib/format'
import { listUsers, EMPTY_USER_FILTERS } from '@/features/users/api'
import type {
  AlertComparatorCode, AlertMetricCode, AlertRuleResponse, AnalyticsRecomputeResult,
} from '@/shared/api/types'
import {
  createAlert, deleteAlert, listAlerts, recomputeAnalytics, updateAlert,
} from '../api'
import {
  ALERT_METRIC_ORDER, AlertMetricBadge, METRIC_IS_PERCENT, METRIC_NEEDS_WINDOW,
  alertMetricLabel, comparatorLabel,
} from '../vocab'
import { DatePicker } from '@/components/erp/date-picker'

/**
 * Alert rules.
 *
 * ## An alert that fires when you open a page is a tooltip
 *
 * PollSphere evaluated its rules in the browser, when somebody happened to open
 * the relevant drawer. That is not an alert — it tells you about a problem at
 * the moment you were already looking at the problem. These are evaluated by the
 * nightly job and delivered by email to a named person, so a budget that runs
 * out on a Saturday reaches somebody on Sunday morning rather than whenever
 * anybody next clicks.
 *
 * ## The cooldown is not a nicety
 *
 * A rule on "coverage below 40%" is true every night until somebody fixes it.
 * Without a cooldown the recipient gets the same email every morning for a
 * month, learns to filter the sender, and the next real alert is never read.
 *
 * ## Percentages are typed as percentages
 *
 * `COVERAGE`, `DROP_RATE` and `SPEND_PCT_OF_BUDGET` are fractions on the wire.
 * The form takes 40 and sends 0.4. Nobody has ever meant "alert me when coverage
 * falls below 0.4 percent", and a field that accepts the wrong one silently is
 * a rule that never fires.
 */
export function ProgramAlertsScreen() {
  const canRead = useCan('program:analytics')
  const canWrite = useCan('program:update')
  const [editing, setEditing] = useState<AlertRuleResponse | 'new' | null>(null)
  const [busy, setBusy] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  const { data, error: loadError, loading, reload } = useLoad<AlertRuleResponse[]>(
    () => listAlerts(), [],
  )

  if (!canRead) return <Forbidden what="programme analytics" />

  const rules = data ?? []

  const toggle = async (r: AlertRuleResponse) => {
    setBusy(r.id); setError(null)
    try {
      await updateAlert(r.id, { enabled: !r.enabled })
      await reload()
    } catch (e) { setError(explain(e)) } finally { setBusy(null) }
  }

  const remove = async (r: AlertRuleResponse) => {
    setBusy(r.id); setError(null)
    try {
      await deleteAlert(r.id)
      await reload()
    } catch (e) { setError(explain(e)) } finally { setBusy(null) }
  }

  return (
    <div className="space-y-4">
      <SectionHeader
        title="Alerts"
        description="Evaluated nightly and delivered by email. Nothing here depends on somebody having a page open."
        right={canWrite && (
          <button type="button" className="erp-btn-primary" onClick={() => setEditing('new')}>
            <Plus className="h-3.5 w-3.5" /> New rule
          </button>
        )}
      />

      <ErrorStrip message={error ?? loadError} />

      <Panel title="Rules" bodyClassName="p-0">
        {loading && !data
          ? <div className="p-4"><LoadingBlock label="Loading rules…" /></div>
          : (
            <ErpTable head={(
              <>
                <Th>Rule</Th>
                <Th>Scope</Th>
                <Th>Condition</Th>
                <Th>Recipient</Th>
                <Th className="text-right">Cooldown</Th>
                <Th>Last fired</Th>
                <Th className="text-right">&nbsp;</Th>
              </>
            )}>
              {rules.length === 0
                ? <EmptyRow colSpan={7} message="No alert rules. Nothing is being watched." />
                : rules.map((r) => (
                  <tr key={r.id} className={r.enabled ? undefined : 'opacity-55'}>
                    <Td>
                      <div className="font-medium">{r.name}</div>
                      <AlertMetricBadge code={r.metricCode} />
                    </Td>
                    <Td className="text-erp-xs">
                      {r.programId
                        ? (r.programName ?? r.programId)
                        : <span className="text-muted-foreground">Whole portfolio</span>}
                    </Td>
                    <Td className="text-erp-xs">
                      {alertMetricLabel(r.metricCode)} {comparatorLabel(r.comparatorCode)}{' '}
                      <span className="font-medium tabular-nums">
                        {formatThreshold(r.metricCode, r.threshold)}
                      </span>
                      {r.windowDays != null && <> over {r.windowDays} days</>}
                      {r.lastValue != null && (
                        <div className="text-muted-foreground">
                          last evaluated at {formatThreshold(r.metricCode, r.lastValue)}
                        </div>
                      )}
                    </Td>
                    <Td className="text-erp-xs">{r.recipientEmail ?? r.recipientUserId}</Td>
                    <Td className="text-right tabular-nums">{r.cooldownHours} h</Td>
                    <Td className="text-erp-xs text-muted-foreground">
                      {r.lastFiredAt ? fmtDateTime(r.lastFiredAt) : 'never'}
                    </Td>
                    <Td className="text-right">
                      {canWrite && (
                        <div className="inline-flex gap-1">
                          <button type="button" className="erp-btn" disabled={busy === r.id}
                            onClick={() => void toggle(r)}
                            aria-label={r.enabled ? 'Disable' : 'Enable'}
                            title={r.enabled ? 'Disable this rule' : 'Enable this rule'}>
                            {r.enabled ? <Bell className="h-3.5 w-3.5" /> : <BellOff className="h-3.5 w-3.5" />}
                          </button>
                          <button type="button" className="erp-btn" onClick={() => setEditing(r)}
                            aria-label="Edit">
                            <Pencil className="h-3.5 w-3.5" />
                          </button>
                          <button type="button" className="erp-btn" disabled={busy === r.id}
                            onClick={() => void remove(r)} aria-label="Delete">
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      )}
                    </Td>
                  </tr>
                ))}
            </ErpTable>
          )}
      </Panel>

      <Note tone="info">
        A rule with no programme watches the whole portfolio under the recipient's own
        scope — a ward coordinator's portfolio rule is about their ward. Two people with
        the same rule can therefore be alerted at different times, and both are right.
      </Note>

      {canWrite && <RecomputePanel />}

      {editing && (
        <AlertDialog
          rule={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); void reload() }}
        />
      )}
    </div>
  )
}

function formatThreshold(metric: AlertMetricCode, value: string | number) {
  const n = Number(value)
  if (Number.isNaN(n)) return String(value)
  return METRIC_IS_PERCENT.includes(metric) ? `${(n * 100).toFixed(0)}%` : fmtNumber(n)
}

// ── the form ────────────────────────────────────────────────────────────────

function AlertDialog({ rule, onClose, onSaved }: {
  rule: AlertRuleResponse | null
  onClose: () => void
  onSaved: () => void
}) {
  const me = useSession((s) => s.user)
  const [name, setName] = useState(rule?.name ?? '')
  const [metric, setMetric] = useState<AlertMetricCode>(rule?.metricCode ?? 'COVERAGE')
  const [comparator, setComparator] = useState<AlertComparatorCode>(rule?.comparatorCode ?? 'BELOW')
  const [threshold, setThreshold] = useState(() => initialThreshold(rule))
  const [windowDays, setWindowDays] = useState(String(rule?.windowDays ?? 7))
  const [cooldown, setCooldown] = useState(String(rule?.cooldownHours ?? 24))
  const [recipient, setRecipient] = useState<{ value: string; label: string }[]>(
    rule
      ? [{ value: rule.recipientUserId, label: rule.recipientEmail ?? rule.recipientUserId }]
      : me ? [{ value: me.id, label: me.email ?? me.phone }] : [],
  )
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const isPercent = METRIC_IS_PERCENT.includes(metric)
  const needsWindow = METRIC_NEEDS_WINDOW.includes(metric)

  // A metric change can make the typed threshold meaningless — 40 meaning "40%"
  // is not 40 meaning "40 duplicate people". Clear it rather than silently
  // reinterpreting it.
  //
  // This has to apply when EDITING too, which is the case with something to
  // protect: an existing DUPLICATE_COUNT rule at 40 people, switched to
  // COVERAGE, would keep "40" in a field now labelled "%" and send 0.4. The
  // first mount is skipped so opening the dialog does not blank the value the
  // rule already has.
  const firstRender = useRef(true)
  useEffect(() => {
    if (firstRender.current) { firstRender.current = false; return }
    setThreshold('')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [metric])

  const submit = async () => {
    setSaving(true); setError(null)
    const raw = Number(threshold)
    const body = {
      name: name.trim(),
      metricCode: metric,
      comparatorCode: comparator,
      threshold: isPercent ? raw / 100 : raw,
      windowDays: needsWindow ? Number(windowDays) : null,
      recipientUserId: recipient[0]?.value,
      cooldownHours: Number(cooldown),
      enabled: rule?.enabled ?? true,
      programId: rule?.programId ?? null,
    }
    try {
      if (rule) await updateAlert(rule.id, body)
      else await createAlert(body)
      onSaved()
    } catch (e) { setError(explain(e)) } finally { setSaving(false) }
  }

  const valid = name.trim().length > 0
    && threshold.trim().length > 0
    && !Number.isNaN(Number(threshold))
    && recipient.length === 1
    && Number(cooldown) > 0

  return (
    <Modal
      open
      onClose={onClose}
      title={rule ? 'Edit alert rule' : 'New alert rule'}
      description="The nightly job evaluates this and emails the recipient."
      width="md"
      footer={(
        <>
          <button type="button" className="erp-btn" onClick={onClose}>Cancel</button>
          <button type="button" className="erp-btn-primary" disabled={!valid || saving}
            onClick={() => void submit()}>
            {saving ? 'Saving…' : rule ? 'Save' : 'Create'}
          </button>
        </>
      )}
    >
      <div className="space-y-3">
        <ErrorStrip message={error} />

        <Field label="Name" required hint="What the recipient will see in the subject line.">
          <input className="erp-input w-full" value={name} onChange={(e) => setName(e.target.value)}
            placeholder="Coverage falling in my ward" />
        </Field>

        <div className="grid gap-3" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))' }}>
          <Field label="Metric" required>
            <select className="erp-input w-full" value={metric}
              onChange={(e) => setMetric(e.target.value as AlertMetricCode)}>
              {ALERT_METRIC_ORDER.map((m) => (
                <option key={m} value={m}>{alertMetricLabel(m)}</option>
              ))}
            </select>
          </Field>
          <Field label="Fires when" required>
            <select className="erp-input w-full" value={comparator}
              onChange={(e) => setComparator(e.target.value as AlertComparatorCode)}>
              <option value="BELOW">falls below</option>
              <option value="ABOVE">rises above</option>
            </select>
          </Field>
          <Field
            label={isPercent ? 'Threshold (%)' : 'Threshold'}
            required
            hint={isPercent ? 'Type 40 for forty percent.' : undefined}
          >
            <input className="erp-input w-full" inputMode="decimal" value={threshold}
              onChange={(e) => setThreshold(e.target.value)} />
          </Field>
        </div>

        {needsWindow && (
          <Field label="Window (days)" required
            hint={`${alertMetricLabel(metric)} is measured over a window, not at a moment.`}>
            <input className="erp-input w-full" inputMode="numeric" value={windowDays}
              onChange={(e) => setWindowDays(e.target.value)} />
          </Field>
        )}

        <Field label="Recipient" required
          hint="One person. An alert addressed to everybody is addressed to nobody.">
          <AsyncMultiSelect
            label="Recipient"
            selected={recipient}
            // Replacing rather than appending — the API takes one recipient, and
            // a control that silently keeps the first pick would send the alert
            // to somebody the user thought they had changed.
            onChange={(next) => setRecipient(next.slice(-1))}
            search={(term) =>
              listUsers({ ...EMPTY_USER_FILTERS, q: term }, 0, 20)
                // This rule delivers by EMAIL, so an account without one cannot receive it —
                // the phone falls in as the label so the row is still identifiable, and the
                // server rejects a recipient with no address.
                .then((p) => p.content.map((u) => ({ value: u.id, label: u.email ?? u.phone, sub: u.name })))}
            placeholder="Search by name or email"
          />
        </Field>

        <Field label="Cooldown (hours)" required
          hint="How long before this rule may fire again. A rule with no cooldown emails the same person every night until somebody fixes the thing, and they stop reading it.">
          <input className="erp-input w-full" inputMode="numeric" value={cooldown}
            onChange={(e) => setCooldown(e.target.value)} />
        </Field>
      </div>
    </Modal>
  )
}

function initialThreshold(rule: AlertRuleResponse | null): string {
  if (!rule) return ''
  const n = Number(rule.threshold)
  if (Number.isNaN(n)) return ''
  return METRIC_IS_PERCENT.includes(rule.metricCode) ? String(n * 100) : String(n)
}

// ── recompute ───────────────────────────────────────────────────────────────

/**
 * Re-run the nightly rollup for a date range.
 *
 * Gated on `program:update`, not `program:analytics`. A read permission that can
 * start a batch job is not a read permission, and somebody with a dashboard
 * login should not be able to queue work on the database by clicking a button
 * on a reporting screen.
 */
function RecomputePanel() {
  const [from, setFrom] = useState('')
  const [to, setTo] = useState('')
  const [running, setRunning] = useState(false)
  const [result, setResult] = useState<AnalyticsRecomputeResult | null>(null)
  const [error, setError] = useState<string | null>(null)

  const run = async () => {
    setRunning(true); setError(null); setResult(null)
    try {
      setResult(await recomputeAnalytics(from || undefined, to || undefined))
    } catch (e) { setError(explain(e)) } finally { setRunning(false) }
  }

  return (
    <Panel
      title="Recompute"
      description="Re-runs the nightly rollup over a date range. Alerts fire from the recomputed figures."
    >
      <ErrorStrip message={error} />
      <div className="flex flex-wrap items-end gap-3">
        <Field label="From" hint="Leave both blank for yesterday.">
          <DatePicker value={from} onChange={setFrom} />
        </Field>
        <Field label="To">
          <DatePicker value={to} onChange={setTo} />
        </Field>
        <button type="button" className="erp-btn" onClick={() => void run()} disabled={running}>
          <RefreshCw className={running ? 'h-3.5 w-3.5 animate-spin' : 'h-3.5 w-3.5'} />
          {running ? 'Recomputing…' : 'Recompute'}
        </button>
      </div>
      {result && (
        <div className="mt-3 rounded-md border border-border bg-muted/30 p-3 text-erp-xs">
          <div className="font-medium text-erp-sm">Recomputed {result.from} → {result.to}</div>
          <ul className="mt-1 space-y-0.5 text-muted-foreground">
            <li>{fmtNumber(result.programmesTouched)} programmes touched</li>
            <li>{fmtNumber(result.overlapPairs)} overlap pairs rebuilt</li>
            <li>{fmtNumber(result.alertsFired)} alerts fired</li>
            <li>{fmtNumber(result.prunedRollupRows)} stale rollup rows pruned</li>
          </ul>
          {result.alertsFired > 0 && (
            <p className="mt-2">
              Those emails have been sent. A recompute over a range that has already been
              alerted on will not re-send within each rule's cooldown.
            </p>
          )}
        </div>
      )}
    </Panel>
  )
}
